import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Box, Stack, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Alert, { AlertColor } from "@mui/material/Alert";
import { CancelButton } from "../../shared/CancelButton";
import { TabView } from "../../shared/TabView";
import { Snackbar } from "../../shared/Snackbar";
import { MessageCard, MessageType } from "../../shared/MessageCard";
import { ReusableCustomQuestionGroups } from "./QuestionGroups/ReusableCustomQuestionGroups";
import { ReusableCustomQuestionFields } from "./QuestionFields/ReusableCustomQuestionFields";
import { useCustomQuestionGroupsByCustomer } from "../../data/CustomWorkflowQuestion/useCustomQuestionGroupsByCustomer";
import { useCustomQuestionsByCustomer } from "../../data/CustomWorkflowQuestion/useCustomQuestionsByCustomer";
import { AccessControlContext } from "../../contexts/AccessControlContext";

interface Props {
  customerId: number;
  open?: boolean;
  onClose: () => void;
}

export function ReusableCustomQuestionLibrary(props: Props) {
  const { t } = useTranslation();

  const { customerId, open, onClose } = props;

  const accessControlContext = useContext(AccessControlContext);
  const canEditWorkflow = accessControlContext.canI("edit", "workflow", customerId);

  const customQuestionGroups = useCustomQuestionGroupsByCustomer(customerId);
  const customQuestionFields = useCustomQuestionsByCustomer(customerId);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const handleSnackbarClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  useEffect(() => {
    if (!showSnackbar) setSnackbarText("");
  }, [showSnackbar]);

  function handleSuccess(successMessage: string) {
    setSnackbarSeverity("success");
    refetchData();
    setSnackbarText(successMessage);
    setShowSnackbar(true);
  }

  function handleError(errorMessage: string) {
    setSnackbarSeverity("error");
    setSnackbarText(errorMessage);
    setShowSnackbar(true);
  }

  function refetchData() {
    customQuestionGroups.refetch();
    customQuestionFields.refetch();
  }

  const header = (
    <Stack direction="row" alignItems="center">
      {t("customQuestionLibrary.customQuestionLibrary")}
      <Tooltip
        title={<Box sx={{ whiteSpace: "pre-line" }}>{t("customQuestionLibrary.customQuestionLibraryTooltip")}</Box>}
      >
        <HelpOutlineIcon color={"action"} sx={{ ml: 1 }} />
      </Tooltip>
    </Stack>
  );

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} marginTop={0}>
      <CancelButton onClick={onClose}>{t("common.close")}</CancelButton>
    </Stack>
  );

  return (
    <>
      <Dialog
        header={header}
        visible={open}
        onHide={onClose}
        footer={footer}
        breakpoints={{ "1960px": "75vw", "1300px": "90vw" }}
        style={{ width: "75vw", height: "85vh" }}
        draggable={false}
      >
        <TabView tabLabels={[t("customQuestions.customQuestions"), t("customQuestions.questionGroups")]}>
          {customQuestionFields.isError ? (
            <MessageCard
              title={t("common.dataFetchError_short", {
                val: t("customQuestions.customQuestions"),
              })}
              content={t("common.dataFetchError", {
                val: t("customQuestions.customQuestions"),
              })}
              messageType={MessageType.Error}
            />
          ) : (
            <ReusableCustomQuestionFields
              customerId={customerId}
              customQuestionFields={customQuestionFields.data}
              isLoading={customQuestionFields.isLoading}
              onSuccess={handleSuccess}
              displayMode="edit"
              canEditWorkflow={canEditWorkflow?.valueOf()}
            />
          )}
          {customQuestionGroups.isError ? (
            <MessageCard
              title={t("common.dataFetchError_short", {
                val: t("customQuestions.questionGroups"),
              })}
              content={t("common.dataFetchError", {
                val: t("customQuestions.questionGroups"),
              })}
              messageType={MessageType.Error}
            />
          ) : (
            <ReusableCustomQuestionGroups
              customerId={customerId}
              customQuestionGroups={customQuestionGroups.data}
              isLoading={customQuestionGroups.isLoading}
              onSuccess={handleSuccess}
              onError={handleError}
              canEditWorkflow={canEditWorkflow?.valueOf()}
            />
          )}
        </TabView>
      </Dialog>
      <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
    </>
  );
}
